import React, { useState, useEffect, useRef } from 'react';
import { Search, Menu, Shield } from 'lucide-react';
import { getCurrentUser, passkeySupported, registerPasskeyForCurrentUser } from '../services/auth';
import Control4TvToggle from './Control4TvToggle';
import logoCp from '../logo-cp.svg';
import MfaEnrollModal from './MfaEnrollModal';

const LOCATION_CACHE_KEY = 'tcpDashboardLocation';

export default function TopBar({ navigateTo, onMenuToggle }) {
  const user = getCurrentUser();
  const [orderId, setOrderId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [weather, setWeather] = useState(null);
  const [forecast, setForecast] = useState([]);
  const [showForecast, setShowForecast] = useState(false);
  const [locationName, setLocationName] = useState('');
  const [locationZip, setLocationZip] = useState('');
  const [passkeyStatus, setPasskeyStatus] = useState(null); // null | 'registering' | 'done' | 'error'
  const [mfaModalOpen, setMfaModalOpen] = useState(false);
  const forecastRef = useRef(null);

  useEffect(() => {
    if (!showForecast) return;
    const handleClick = (e) => { if (forecastRef.current && !forecastRef.current.contains(e.target)) setShowForecast(false); };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [showForecast]);

  const fetchWeather = (lat, lon) => {
    fetch(`https://api.open-meteo.com/v1/forecast?latitude=${lat}&longitude=${lon}&current_weather=true&daily=weathercode,temperature_2m_max,temperature_2m_min,precipitation_probability_max,windspeed_10m_max,windgusts_10m_max,winddirection_10m_dominant&temperature_unit=fahrenheit&windspeed_unit=mph&timezone=America%2FNew_York&forecast_days=7`)
      .then(r => r.json())
      .then(data => {
        setWeather(data.current_weather);
        if (data.daily) {
          const days = data.daily.time.map((date, i) => ({
            date,
            high: Math.round(data.daily.temperature_2m_max[i]),
            low: Math.round(data.daily.temperature_2m_min[i]),
            code: data.daily.weathercode[i],
            precip: data.daily.precipitation_probability_max[i],
            wind: Math.round(data.daily.windspeed_10m_max[i]),
            gusts: Math.round(data.daily.windgusts_10m_max[i]),
            windDir: data.daily.winddirection_10m_dominant[i],
          }));
          setForecast(days);
        }
      })
      .catch(() => {});
  };

  const fetchLocation = (lat, lon) => {
    fetch(`https://nominatim.openstreetmap.org/reverse?lat=${lat}&lon=${lon}&format=json`)
      .then(r => r.json())
      .then(data => {
        const city = data.address?.city || data.address?.town || data.address?.village || data.address?.county || '';
        const stateRaw = data.address?.state_code || data.address?.state || '';
        // Nominatim sometimes returns "US-MA" — take only the part after the dash
        const stateCode = stateRaw.includes('-') ? stateRaw.split('-').pop() : stateRaw;
        const STATE_ABBR = { 'Alabama':'AL','Alaska':'AK','Arizona':'AZ','Arkansas':'AR','California':'CA','Colorado':'CO','Connecticut':'CT','Delaware':'DE','Florida':'FL','Georgia':'GA','Hawaii':'HI','Idaho':'ID','Illinois':'IL','Indiana':'IN','Iowa':'IA','Kansas':'KS','Kentucky':'KY','Louisiana':'LA','Maine':'ME','Maryland':'MD','Massachusetts':'MA','Michigan':'MI','Minnesota':'MN','Mississippi':'MS','Missouri':'MO','Montana':'MT','Nebraska':'NE','Nevada':'NV','New Hampshire':'NH','New Jersey':'NJ','New Mexico':'NM','New York':'NY','North Carolina':'NC','North Dakota':'ND','Ohio':'OH','Oklahoma':'OK','Oregon':'OR','Pennsylvania':'PA','Rhode Island':'RI','South Carolina':'SC','South Dakota':'SD','Tennessee':'TN','Texas':'TX','Utah':'UT','Vermont':'VT','Virginia':'VA','Washington':'WA','West Virginia':'WV','Wisconsin':'WI','Wyoming':'WY','District of Columbia':'DC' };
        const state = stateCode.length === 2 ? stateCode : (STATE_ABBR[stateCode] || stateCode);
        const zip = data.address?.postcode || '';
        const name = city && state ? `${city}, ${state}` : city || state;
        setLocationName(name);
        setLocationZip(zip);
        try {
          localStorage.setItem(LOCATION_CACHE_KEY, JSON.stringify({ lat, lon, name, zip }));
        } catch {}
      })
      .catch(() => {});
  };

  const acquireLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          fetchWeather(coords.latitude, coords.longitude);
          fetchLocation(coords.latitude, coords.longitude);
        },
        () => {
          // fallback to Crisfield — don't cache, so a one-time deny doesn't pin them here forever
          fetchWeather(38.2, -75.8);
          setLocationName('Crisfield, MD');
          setLocationZip('21817');
        }
      );
    } else {
      fetchWeather(38.2, -75.8);
      setLocationName('Crisfield, MD');
      setLocationZip('21817');
    }
  };

  const refreshLocation = (e) => {
    e?.stopPropagation();
    try { localStorage.removeItem(LOCATION_CACHE_KEY); } catch {}
    setShowForecast(false);
    acquireLocation();
  };

  useEffect(() => {
    // Use cached location if we have one — avoids re-prompting on every dashboard load
    // (mobile browsers don't persist the geolocation permission like desktop Chrome does).
    try {
      const cached = JSON.parse(localStorage.getItem(LOCATION_CACHE_KEY) || 'null');
      if (cached && typeof cached.lat === 'number' && typeof cached.lon === 'number') {
        fetchWeather(cached.lat, cached.lon);
        if (cached.name) setLocationName(cached.name);
        if (cached.zip) setLocationZip(cached.zip);
        return;
      }
    } catch {}
    acquireLocation();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const windCompass = (deg) => {
    const dirs = ['N','NNE','NE','ENE','E','ESE','SE','SSE','S','SSW','SW','WSW','W','WNW','NW','NNW'];
    return dirs[Math.round(deg / 22.5) % 16];
  };

  const weatherIcon = (code) => {
    if (code === 0) return '☀️';
    if (code <= 2) return '⛅';
    if (code <= 3) return '☁️';
    if (code <= 49) return '🌫️';
    if (code <= 67) return '🌧️';
    if (code <= 77) return '❄️';
    if (code <= 82) return '🌦️';
    if (code <= 99) return '⛈️';
    return '🌡️';
  };

  const handleOrderSubmit = () => {
    if (orderId) { navigateTo('order-details', orderId); setOrderId(''); }
  };

  const handleCustomerSearch = () => {
    const q = searchQuery.trim();
    if (!q) return;
    setSearchQuery('');
    // UPS tracking number
    if (/^1Z/i.test(q)) {
      navigateTo('customer-search', null, { trackingNum: q });
    // All digits → order number
    } else if (/^\d{4,9}$/.test(q)) {
      navigateTo('order-details', q);
    // Email
    } else if (q.includes('@')) {
      navigateTo('customer-search', null, { email: q });
    // Phone (10+ digits when stripped)
    } else if (/^[\d\s\-\(\)\.]+$/.test(q) && q.replace(/\D/g, '').length >= 10) {
      navigateTo('customer-search', null, { phoneNumber: q.replace(/\D/g, '') });
    // Name: two words → first + last, one word → last name
    } else {
      const parts = q.split(/\s+/);
      if (parts.length >= 2) {
        navigateTo('customer-search', null, { firstName: parts[0], lastName: parts.slice(1).join(' ') });
      } else {
        navigateTo('customer-search', null, { lastName: q });
      }
    }
  };

  return (
    <div className="topbar">
      {/* Mobile: hamburger + logo */}
      <button className="hamburger-btn" onClick={onMenuToggle} aria-label="Menu">
        <Menu size={22} />
      </button>
      <img src={logoCp} alt="The Crab Place" className="topbar-mobile-logo" style={{ height: 32 }} />

      {/* Desktop: search inputs */}
      <input className="topbar-desktop"
        type="text"
        placeholder="Enter Order ID"
        value={orderId}
        onChange={e => setOrderId(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && handleOrderSubmit()}
        style={{ width: 140 }}
      />
      <button className="btn btn-primary topbar-desktop" onClick={handleOrderSubmit}>Submit</button>

      <div className="topbar-desktop" style={{ width: 1, height: 24, background: 'var(--gray-200)', margin: '0 4px' }} />

      <input
        className="topbar-desktop"
        type="text"
        placeholder="Name, email, phone, tracking #..."
        value={searchQuery}
        onChange={e => setSearchQuery(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && handleCustomerSearch()}
        style={{ width: 220 }}
      />

      <button className="btn btn-navy topbar-desktop" onClick={handleCustomerSearch} style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <Search size={13} /> Search
      </button>

      {user?.staffName && (
        <div className="topbar-user-block" style={{ marginLeft: 'auto', marginRight: 8, display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
          <span onClick={() => navigateTo(user.staffName === 'Greg Cain' ? 'todo-greg' : 'my-board')} style={{ fontSize: 13, fontWeight: 600, whiteSpace: 'nowrap', cursor: 'pointer', color: 'var(--navy)' }}
            title="Go to my To-Do page">
            Hi, {user.staffName.split(' ')[0]}
          </span>
          {passkeySupported() && passkeyStatus !== 'done' && (
            <button
              onClick={async () => {
                setPasskeyStatus('registering');
                try {
                  await registerPasskeyForCurrentUser();
                  setPasskeyStatus('done');
                } catch (e) {
                  console.error('[passkey] Registration failed:', e);
                  if (e.name === 'NotAllowedError') {
                    setPasskeyStatus(null); // user cancelled, reset
                  } else if (e.message?.includes('session') || e.message?.includes('log in')) {
                    // Session not in DB — need to re-login
                    setPasskeyStatus('relogin');
                  } else {
                    setPasskeyStatus('error');
                  }
                }
              }}
              disabled={passkeyStatus === 'registering'}
              title={passkeyStatus === 'relogin' ? 'Log out and back in, then try again' : passkeyStatus === 'error' ? 'Failed — click to retry' : 'Register a passkey for this device'}
              style={{
                background: passkeyStatus === 'error' || passkeyStatus === 'relogin' ? 'var(--red)' : 'var(--orange)',
                color: '#fff', border: 'none', borderRadius: 4, padding: '3px 8px',
                fontSize: 10, fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                fontFamily: 'inherit', opacity: passkeyStatus === 'registering' ? 0.6 : 1,
              }}
            >
              {passkeyStatus === 'registering' ? 'Setting up...' :
               passkeyStatus === 'relogin' ? 'Re-login first' :
               passkeyStatus === 'error' ? 'Retry Passkey' : 'Add Passkey'}
            </button>
          )}
        </div>
      )}
      <span className="topbar-tvs"><Control4TvToggle /></span>
      {weather && (
        <span
          ref={forecastRef}
          onClick={() => setShowForecast(!showForecast)}
          className="topbar-weather"
          style={{ marginLeft: user?.staffName ? 0 : 'auto', marginRight: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0, cursor: 'pointer', position: 'relative' }}>
          {locationName && (
            <span className="topbar-weather-location" style={{ fontSize: 12, color: 'var(--gray-500)', fontWeight: 500 }}>
              {locationName}<span className="topbar-desktop">{locationZip ? ` ${locationZip}` : ''}</span>
            </span>
          )}
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
            {weatherIcon(weather.weathercode)} {Math.round(weather.temperature)}°F
          </span>
          {showForecast && forecast.length > 0 && (
            <div onClick={e => e.stopPropagation()} style={{
              position: 'absolute', top: '100%', right: 0, marginTop: 8,
              background: '#fff', border: '1px solid var(--gray-200)', borderRadius: 10,
              boxShadow: '0 8px 24px rgba(0,0,0,0.12)', padding: '12px 0', zIndex: 9999,
              minWidth: 420,
            }}>
              <div style={{ padding: '0 16px 8px', fontSize: 12, fontWeight: 700, color: 'var(--navy)', borderBottom: '1px solid var(--gray-100)' }}>
                7-Day Forecast — {locationName}
              </div>
              {forecast.map((day, i) => {
                const d = new Date(day.date + 'T12:00:00');
                const dayName = i === 0 ? 'Today' : d.toLocaleDateString('en-US', { weekday: 'short' });
                const dateStr = d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                const windColor = day.wind >= 20 ? '#c53030' : day.wind >= 15 ? '#dd6b20' : 'var(--gray-600)';
                return (
                  <div key={day.date} style={{
                    display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px',
                    fontSize: 12, borderBottom: i < forecast.length - 1 ? '1px solid var(--gray-50)' : 'none',
                    background: i === 0 ? '#f0f7ff' : 'transparent',
                  }}>
                    <span style={{ width: 42, fontWeight: i === 0 ? 700 : 600, color: 'var(--navy)' }}>{dayName}</span>
                    <span style={{ width: 48, color: 'var(--gray-500)', fontSize: 11 }}>{dateStr}</span>
                    <span style={{ fontSize: 16, width: 22, textAlign: 'center' }}>{weatherIcon(day.code)}</span>
                    <span style={{ width: 65, fontWeight: 600 }}>
                      <span style={{ color: '#c53030' }}>{day.high}°</span>
                      <span style={{ color: 'var(--gray-400)', margin: '0 2px' }}>/</span>
                      <span style={{ color: '#2b6cb0' }}>{day.low}°</span>
                    </span>
                    <span style={{ width: 105, fontSize: 10, color: windColor, fontWeight: day.wind >= 15 ? 700 : 500 }}>
                      🌬️ {day.wind}{day.gusts > day.wind + 5 ? `–${day.gusts}` : ''} mph {windCompass(day.windDir)}
                    </span>
                    {day.precip > 0 && (
                      <span style={{ width: 35, fontSize: 10, color: '#3182ce' }}>💧{day.precip}%</span>
                    )}
                  </div>
                );
              })}
              <div style={{ padding: '10px 16px 2px', borderTop: '1px solid var(--gray-100)', textAlign: 'right' }}>
                <button
                  onClick={refreshLocation}
                  style={{
                    background: 'none', border: 'none', padding: 0, cursor: 'pointer',
                    color: 'var(--orange)', fontWeight: 600, fontSize: 11, fontFamily: 'inherit',
                  }}
                  title="Clear cached location and re-detect">
                  📍 Update location
                </button>
              </div>
            </div>
          )}
        </span>
      )}
      <span className="topbar-refresh topbar-desktop" onClick={() => window.location.reload()} style={{ marginLeft: 0, cursor: 'pointer' }}>↻ Refresh</span>
      {/* Security/MFA button + enrollment modal. It sits IN the bar, after Refresh. It used
          to be position:fixed bottom-right, but the bar is the containing block for fixed
          children, so it landed on the bar's own corner, on top of Refresh (Matt, Oct 13, 2026). */}
      <button
        onClick={() => setMfaModalOpen(true)}
        title="Two-factor authentication settings"
        aria-label="Two-factor authentication settings"
        style={{ flex: '0 0 auto', width: 28, height: 28, borderRadius: '50%', background: 'var(--navy, #00395C)', color: '#fff', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', marginLeft: 4 }}>
        <Shield size={14} />
      </button>
      {mfaModalOpen && <MfaEnrollModal onClose={() => setMfaModalOpen(false)} />}
    </div>
  );
}